import IcNodata from "@/assets/icons/ic-nodata.svg";
import IconPlus from "@/assets/icons/ic-plus.svg";
import { ConfirmDialog } from "@/components/common/confirm-dialog";
import { SearchInput } from "@/components/common/search-input";
import { Button } from "@/components/ui/button";
import Pagination from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TruncatedTooltip } from "@/components/ui/truncated-tooltip";
import { toast } from "@/hooks/use-toast";
import { usePermissions } from "@/lib/permissions";
import { formatDate } from "@/lib/utils";
import {
  AdminAxNewsService,
  ICategory,
  IGetListReq,
  IGetListRes,
  IStatus,
} from "@/services/ax-news-service";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const CATEGORY_CONFIG: Record<ICategory, { label: string }> = {
  ai_use_case: {
    label: "AI Use Case",
  },
  ax_trend: {
    label: "AX Trend",
  },
  pwc_digital_solutions: {
    label: "PwC Digital Solutions",
  },
};

const STATUS_CONFIG: Record<IStatus, { label: string; bg: string; text: string }> = {
  scheduled: {
    label: "예약됨",
    bg: "bg-[#FFAA72]",
    text: "text-[#000000]",
  },
  published: {
    label: "게시됨",
    bg: "bg-[#43946C]",
    text: "text-[#000000]",
  },
};

const CATEGORY_FILTER_OPTIONS = [
  { value: "all", label: "전체 카테고리" },
  { value: "ai_use_case", label: "AI Use Case" },
  { value: "ax_trend", label: "AX Trend" },
  { value: "pwc_digital_solutions", label: "PwC Digital Solutions" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "전체 상태" },
  { value: "scheduled", label: "예약됨" },
  { value: "published", label: "게시됨" },
];

const PAGE_SIZE = 10;

const AxNewsListPage = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canCreate = can.create("ax_trend_hub");
  const canUpdate = can.update("ax_trend_hub");
  const canDelete = can.delete("ax_trend_hub");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<ICategory | "all">("all");
  const [statusFilter, setStatusFilter] = useState<IStatus | "all">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [articles, setArticles] = useState<IGetListRes[]>([]);
  const [total, setTotal] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const hasFetchedRef = useRef(false);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const fetchArticles = useCallback(
    async (page: number) => {
      if (hasFetchedRef.current) {
        setIsSearching(true);
      }
      try {
        const params: IGetListReq = {
          page,
          page_size: PAGE_SIZE,
        };
        if (searchValue.trim()) {
          params.search = searchValue.trim();
        }
        if (categoryFilter !== "all") {
          params.category = categoryFilter as ICategory;
        }
        if (statusFilter !== "all") {
          params.status = statusFilter as IStatus;
        }
        const res = await AdminAxNewsService.getList(params);
        if (res.data?.success && res.data?.data) {
          setArticles(res.data.data.items);
          setTotal(res.data.data.total);
        }
      } catch {
        setArticles([]);
        setTotal(0);
      } finally {
        setIsInitialLoading(false);
        setIsSearching(false);
        hasFetchedRef.current = true;
      }
    },
    [searchValue, categoryFilter, statusFilter],
  );

  useEffect(() => {
    fetchArticles(currentPage);
  }, [fetchArticles, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, categoryFilter, statusFilter]);

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchValue("");
    setCurrentPage(1);
  };

  const handleCategoryChange = (value: string) => {
    setCategoryFilter(value as ICategory | "all");
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value as IStatus | "all");
    setCurrentPage(1);
  };

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const handleDeleteClick = (e: React.MouseEvent, articleId: string) => {
    e.stopPropagation();
    setDeleteTargetId(articleId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await AdminAxNewsService.delete(deleteTargetId);
      toast({ description: "게시글이 삭제되었습니다." });
      fetchArticles(currentPage);
    } catch {
      toast({ description: "글 삭제에 실패했습니다.", variant: "destructive" });
    } finally {
      setDeleteTargetId(null);
    }
  };

  const handleEdit = (e: React.MouseEvent, articleId: string) => {
    e.stopPropagation();
    navigate(`/ax-trend-hub/${articleId}/edit`);
  };

  return (
    <>
      {isInitialLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div 
        className="bg-white flex flex-col gap-6 w-full min-w-0"
        style={{ display: isInitialLoading ? "none" : "flex" }}
      >
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Search */}
          <SearchInput
            placeholder="제목 또는 등록자명으로 검색..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSearchValue(e.target.value.trim());
            }}
            onClear={handleClearSearch}
          />

          {/* Category Filter */}
          <Select value={categoryFilter} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="전체 카테고리" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="전체 상태" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canCreate && (
          <Button
            onClick={() => navigate("/ax-trend-hub/create")}
            variant={"default"}
            className="w-[131px] h-[48px] font-bold bg-primary"
          >
            <img src={IconPlus} alt="plus" className="w-5 h-5" />새 게시글
          </Button>
        )}
      </div>

      {/* Record Count */}
      <div className="flex justify-start">
        <span className="text-sm text-[#B0B0B0]">
          전체 {total}개 기사 중 {articles.length}개 표시 중
        </span>
      </div>

      {/* Table */}
      <div
        className={`border border-[#EBEBEB] rounded overflow-x-auto min-w-0 w-full transition-opacity duration-200 ${isSearching ? "pointer-events-none" : ""}`}
      >
        <Table className="min-w-[1050px]">
          <TableHeader>
            <TableRow className="bg-[#EBEBEB]">
              <TableHead className="w-[280px] max-w-[280px]">제목</TableHead>
              <TableHead className="w-[152px]">카테고리</TableHead>
              <TableHead className="w-[121px]">상태</TableHead>
              <TableHead className="w-[121px]">등록자</TableHead>
              <TableHead className="w-[162px]">게시 시간</TableHead>
              <TableHead className="w-[180px] text-right">관리</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {articles.map((article) => {
              const categoryConfig =
                CATEGORY_CONFIG[article.category] || CATEGORY_CONFIG.ai_use_case;
              const statusConfig = STATUS_CONFIG[article.status] || STATUS_CONFIG.scheduled;
              return (
                <TableRow
                  key={article.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/ax-trend-hub/${article.id}`)}
                >
                  <TableCell className="text-[#000000] max-w-[280px] ">
                    <TruncatedTooltip className="text-[16px] font-medium text-[#000000] truncate">
                      {article.title}
                    </TruncatedTooltip>
                  </TableCell>
                  <TableCell className="max-w-[152px]">
                    <div className={`inline-flex items-center h-6 px-2 rounded `}>
                      <span className="text-[16px] text-[#000000]">{categoryConfig.label}</span>
                    </div>
                  </TableCell>
                  <TableCell className="w-[121px]">
                    <div
                      className={`inline-flex items-center gap-1  pl-[10px] pr-[14px] rounded ${statusConfig.bg}`}
                    >
                      <span className="text-[16px] leading-6 text-black">{statusConfig.label}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-[16px] text-[#101828] max-w-[121px] truncate">
                    <TruncatedTooltip>{article.created_by_name}</TruncatedTooltip>
                  </TableCell>
                  <TableCell className="text-[16px] text-[rgba(0,0,0,0.91)] whitespace-nowrap max-w-[162px]">
                    {article.publish_at ? formatDate(article.publish_at, "YYYY-MM-DD HH:mm") : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-[8px]">
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-[80px] h-[32px] rounded text-[12px] font-bold text-[#000000] bg-[#F7DDCF]"
                          onClick={(e) => handleDeleteClick(e, article.id)}
                        >
                          삭제
                        </Button>
                      )}
                      {canUpdate && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-[80px] h-[32px] rounded text-[12px] font-bold text-[#000000] border border-[rgba(0,0,0,0.15)]"
                          onClick={(e) => handleEdit(e, article.id)}
                        >
                          수정
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {articles.length === 0 && !isInitialLoading && !isSearching && (
              <TableRow>
                <TableCell colSpan={6} className="h-[600px] text-center">
                  <div className="flex flex-col items-center justify-center h-full gap-4">
                    <img src={IcNodata} alt="No data" className="" />

                    <p className="text-[32px] text-[#000000E8]">
                      {searchValue || categoryFilter !== "all" || statusFilter !== "all"
                        ? "검색 결과가 없습니다"
                        : "No data"}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {!isInitialLoading && articles.length > 0 && (
        <Pagination
          className="justify-center"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      <ConfirmDialog
        open={deleteTargetId !== null}
        onOpenChange={(open) => !open && setDeleteTargetId(null)}
        iconVariant="neutral"
        icon={
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.5 12.019C1.5 6.22927 6.21023 1.51904 12 1.51904C17.7898 1.51904 22.5 6.22927 22.5 12.019C22.5 17.5557 18.1897 22.0938 12.75 22.481V20.981C17.3624 20.5978 21 16.7284 21 12.019C21 7.05652 16.9629 3.01904 12 3.01904C7.0371 3.01904 3 7.05652 3 12.019C3 16.6943 6.5841 20.5454 11.1489 20.9759L10.1859 22.3512C5.2593 21.488 1.5 17.1892 1.5 12.019ZM12.1267 6.44497C14.2285 6.44497 14.3363 8.36849 14.3533 8.59754C14.4978 11.3104 11.4082 11.3435 11.2624 14.4478V15.3127H12.8262V14.595C12.8262 12.115 16.0086 11.7613 16.0086 8.74477C16.0086 6.03187 13.9526 5.06512 12.1268 5.06512C9.50768 5.06512 7.95735 7.02937 8.00618 9.48052H9.56993C9.56993 9.42074 9.43477 6.44497 12.1267 6.44497ZM13.0466 16.4718H11.0046V18.5138H13.0466V16.4718Z"
              fill="#202020"
            />
          </svg>
        }
        title="게시물 삭제 확인"
        description="해당 게시물을 영구적으로 삭제하시겠습니까?"
        primaryAction={{ label: "네", onClick: handleDeleteConfirm }}
        secondaryAction={{
          label: "아니요",
          onClick: () => setDeleteTargetId(null),
        }}
      />
    </div>
    </>
  );
};

export default AxNewsListPage;
